import type { TourType } from '@/types/tour';
import { memo } from 'react';
import { useTour } from './TourContext';
import { TourOverlay } from './TourOverlay';
import { tourConfig } from './tourConfig';

interface TourWrapperProps {
  children: React.ReactNode;
  onComplete?: (tourId: TourType) => void;
  onStepComplete?: (tourId: TourType, stepIndex: number) => void;
}

export const TourWrapper = memo(
  ({ children, onComplete, onStepComplete }: TourWrapperProps) => {
    const {
      activeTour,
      currentStep: currentStepIndex,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
    } = useTour();

    const tour = activeTour ? tourConfig[activeTour] : null;
    const currentStep = tour?.steps[currentStepIndex];
    const isLastStep = tour
      ? currentStepIndex === tour.steps.length - 1
      : false;

    const handleNext = () => {
      if (!activeTour) return;

      if (isLastStep) {
        completeTour();
        onComplete?.(activeTour);
      } else {
        nextStep();
        if (currentStep) {
          onStepComplete?.(activeTour, currentStepIndex);
        }
      }
    };

    const handleSkip = () => {
      if (!activeTour) return;
      skipTour();
      onComplete?.(activeTour);
    };

    if (!activeTour || !tour || !currentStep) return <>{children}</>;

    return (
      <>
        {children}
        <TourOverlay
          step={currentStep}
          onNext={handleNext}
          onPrev={prevStep}
          onSkip={handleSkip}
          tourId={activeTour}
        />
      </>
    );
  },
);
